import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { FIRST_LABEL_ACTIVE_STATUSES, FIRST_LABEL_STAGES, stageIndexForStatus } from '../firstLabelRun';

/**
 * Full-screen progress gate shown while a project's First Label Analysis
 * runs. The project is not usable (no chats, no documents) until the
 * analysis and the public dataroom are complete, so this replaces the whole
 * workspace content area. Presentational: data comes via props.
 */
// Stable provider-failure categories set by the backend
// (apps.ai.services.provider_errors). Raw run.error never renders here.
const FAILURE_MESSAGES = {
  quota_exhausted: 'dataroom.firstLabel.gate.quotaExhausted',
  invalid_key: 'dataroom.firstLabel.gate.invalidKey',
  rate_limited: 'dataroom.firstLabel.gate.rateLimited',
  provider_down: 'dataroom.firstLabel.gate.providerDown',
};

export function FirstLabelGate({ run, companyName, onRetry, isRetrying }) {
  const { t } = useTranslation();
  if (!run) return null;

  const company = run.company_name || companyName || t('dataroom.firstLabel.gate.thisCompany');

  if (run.status === 'failed') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="first-label-gate-failed">
        <Icon icon="mdi:alert-circle-outline" className="size-12 text-[var(--color-danger)]" />
        <h2 className="text-xl font-semibold text-[var(--color-text)]">
          {t('dataroom.firstLabel.gate.failedHeading', { company })}
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] max-w-md">
          {t(FAILURE_MESSAGES[run.error_category] || 'dataroom.firstLabel.gate.genericFailure')}
        </p>
        {/* Guests get no onRetry: relaunching an analysis is a write. */}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            data-testid="first-label-gate-retry"
            className="cursor-pointer py-2 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
          >
            <Icon icon="mdi:refresh" className="size-4" />
            {isRetrying ? t('dataroom.firstLabel.gate.relaunching') : t('dataroom.firstLabel.gate.retryAnalysis')}
          </button>
        )}
      </div>
    );
  }

  if (!FIRST_LABEL_ACTIVE_STATUSES.has(run.status)) return null;

  const currentIndex = stageIndexForStatus(run.status);

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 px-8" data-testid="first-label-gate">
      <div className="text-center">
        <div
          className="animate-spin inline-block size-8 border-[3px] border-current border-t-transparent text-[var(--color-accent)] rounded-full mb-4"
          role="status"
        >
          <span className="sr-only">{t('dataroom.firstLabel.gate.loading')}</span>
        </div>
        <h2 className="text-xl font-semibold text-[var(--color-text)]">
          {t('dataroom.firstLabel.gate.evaluating', { company })}
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1 max-w-md">
          {t('dataroom.firstLabel.gate.intro')}
        </p>
      </div>

      <ol className="w-full max-w-md flex flex-col gap-0" data-testid="first-label-gate-steps">
        {FIRST_LABEL_STAGES.map((stage, index) => {
          const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'pending';
          return (
            <li key={stage.key} className="flex gap-3 items-start">
              <div className="flex flex-col items-center">
                {state === 'done' && <Icon icon="mdi:check-circle" className="size-5 text-emerald-500" />}
                {state === 'current' && (
                  <span className="mt-0.5 animate-spin inline-block size-4 border-[2px] border-current border-t-transparent text-[var(--color-accent)] rounded-full" />
                )}
                {state === 'pending' && <Icon icon="mdi:circle-outline" className="size-5 text-[var(--color-border)]" />}
                {index < FIRST_LABEL_STAGES.length - 1 && (
                  <span className={`w-px h-6 ${state === 'done' ? 'bg-emerald-300' : 'bg-[var(--color-border)]'}`} />
                )}
              </div>
              <div className="pb-2 text-left">
                <p className={`text-sm font-medium ${state === 'current' ? 'text-[var(--color-accent)]' : state === 'done' ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}>
                  {t(stage.label)}
                </p>
                {state === 'current' && (
                  <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{t(stage.description)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <p className="text-xs text-[var(--color-text-muted)]">
        {t('dataroom.firstLabel.gate.unlockNote')}
      </p>
    </div>
  );
}
